import React from 'react';
import { Thead, Tbody } from '@chakra-ui/react';

import type {
  AvailableCarrier,
  CarrierQuote,
  CarriersVehicle,
  RiskDetailsSync,
} from 'api/quote-marketplace';
import { useAnswerCodes } from '../../hooks/useAnswerCodes';
import { useComparisonTableHelpers, useExpandableItems } from '../../hooks';
import { formatCurrency } from '../../constants';
import { ComparisonTableHeader } from './ComparisonTableHeader';
import type { CarrierSortOption } from '../../hooks';
import {
  VehicleCoveragesSection,
  type SectionKey,
} from './VehicleCoveragesSection';
import { MarketMessagesSection } from './MarketMessagesSection';
import {
  TableWrapper,
  ItemHeaderRow,
  PremiumRow,
  ItemSeparatorRow,
  MonthlyPremiumSection,
} from './SharedTableComponents';

interface CarriersComparisonTableProps {
  availableCarriers: AvailableCarrier[];
  carrierQuotes: CarrierQuote[];
  vehicles: CarriersVehicle[];
  selectedCarrierQuoteId: number | null;
  rfqStatus: string;
  onSelectCarrier: (carrierQuoteId: number) => void;
  onEditCoverages?: (vehicleId?: number) => void;
  onOpenMarketOptions: () => void;
  onGetQuotes: () => void;
  isSelectingCarrier?: boolean;
  isGettingQuotes?: boolean;
  canGetQuotes?: boolean;
  isCapturingRiskDetails?: boolean;
  riskDetailsSync?: RiskDetailsSync;
  provinceCode?: number;
  sortBy?: CarrierSortOption;
  onSortChange?: (sort: CarrierSortOption) => void;
}

const CarriersComparisonTable = ({
  availableCarriers,
  carrierQuotes,
  vehicles,
  selectedCarrierQuoteId,
  rfqStatus,
  onSelectCarrier,
  onEditCoverages,
  onOpenMarketOptions,
  onGetQuotes,
  isSelectingCarrier = false,
  isGettingQuotes = false,
  canGetQuotes = true,
  isCapturingRiskDetails = false,
  riskDetailsSync,
  provinceCode,
  sortBy,
  onSortChange,
}: CarriersComparisonTableProps) => {
  const { getLabel } = useAnswerCodes();

  const { theme, isLoading, getQuoteForCarrier, isCarrierSelected } =
    useComparisonTableHelpers({
      carrierQuotes,
      selectedCarrierQuoteId,
      rfqStatus,
    });

  const { isItemExpanded, toggleItem, expandedSections, toggleSection } =
    useExpandableItems({ items: vehicles });

  const [marketMessagesExpanded, setMarketMessagesExpanded] =
    React.useState(false);

  const getVehicleLabel = (
    vehicle: CarriersVehicle,
    _index: number,
  ): string => {
    return [vehicle.vehicle_model_year, vehicle.make, vehicle.model]
      .filter(Boolean)
      .join(' ');
  };

  const getVehiclePremium = (
    quote: CarrierQuote,
    vehicleIndex: number,
  ): string | null => {
    return quote.vehicle_premiums?.[vehicleIndex] ?? null;
  };

  return (
    <TableWrapper theme={theme}>
      <Thead>
        <ComparisonTableHeader
          availableCarriers={availableCarriers}
          carrierQuotes={carrierQuotes}
          selectedCarrierQuoteId={selectedCarrierQuoteId}
          onSelectCarrier={onSelectCarrier}
          onOpenMarketOptions={onOpenMarketOptions}
          onGetQuotes={onGetQuotes}
          isLoading={isLoading}
          isSelectingCarrier={isSelectingCarrier}
          isGettingQuotes={isGettingQuotes}
          canGetQuotes={canGetQuotes}
          isCapturingRiskDetails={isCapturingRiskDetails}
          riskDetailsSync={riskDetailsSync}
          theme={theme}
          sortBy={sortBy}
          onSortChange={onSortChange}
        />
      </Thead>
      <Tbody>
        {vehicles.map((vehicle, vehicleIndex) => {
          const isVehicleExpanded = isItemExpanded(vehicle.id);

          return (
            <React.Fragment key={vehicle.id}>
              <ItemHeaderRow
                label={getVehicleLabel(vehicle, vehicleIndex)}
                isExpanded={isVehicleExpanded}
                onToggle={() => toggleItem(vehicle.id)}
                onEdit={onEditCoverages ? () => onEditCoverages() : undefined}
                isEditDisabled={isLoading}
                availableCarriers={availableCarriers}
                getQuoteForCarrier={getQuoteForCarrier}
                isCarrierSelected={isCarrierSelected}
                isLoading={isLoading}
                theme={theme}
                getPremiumForQuote={(quote) =>
                  getVehiclePremium(quote, vehicleIndex)
                }
              />

              {isVehicleExpanded && (
                <VehicleCoveragesSection
                  vehicle={vehicle}
                  vehicleIndex={vehicleIndex}
                  availableCarriers={availableCarriers}
                  carrierQuotes={carrierQuotes}
                  selectedCarrierQuoteId={selectedCarrierQuoteId}
                  expandedSections={
                    (expandedSections[vehicle.id] as SectionKey[]) || []
                  }
                  isLoading={isLoading}
                  theme={theme}
                  getLabel={getLabel}
                  onToggleSection={(section) =>
                    toggleSection(vehicle.id, section)
                  }
                  provinceCode={provinceCode}
                />
              )}

              {vehicleIndex < vehicles.length - 1 && !isVehicleExpanded && (
                <ItemSeparatorRow
                  colSpan={availableCarriers.length + 2}
                  borderColor={theme.borderColor}
                />
              )}
            </React.Fragment>
          );
        })}

        <MarketMessagesSection
          availableCarriers={availableCarriers}
          carrierQuotes={carrierQuotes}
          selectedCarrierQuoteId={selectedCarrierQuoteId}
          isExpanded={marketMessagesExpanded}
          onToggle={() => setMarketMessagesExpanded(!marketMessagesExpanded)}
          theme={theme}
        />

        <PremiumRow
          label="Estimated Annual Premium"
          getValue={(quote) =>
            (quote?.status === 'quoted' || quote?.status === 'pending') &&
            quote?.annual_premium != null
              ? formatCurrency(quote.annual_premium)
              : '-'
          }
          isBold
          availableCarriers={availableCarriers}
          getQuoteForCarrier={getQuoteForCarrier}
          isCarrierSelected={isCarrierSelected}
          isLoading={isLoading}
          theme={theme}
        />

        <MonthlyPremiumSection
          availableCarriers={availableCarriers}
          getQuoteForCarrier={getQuoteForCarrier}
          isCarrierSelected={isCarrierSelected}
          isLoading={isLoading}
          theme={theme}
        />
      </Tbody>
    </TableWrapper>
  );
};

export default CarriersComparisonTable;
